import { Tabs, Tab, Typography, Grid, createStyles, makeStyles, Theme } from "@material-ui/core";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useDeliveryAddressCookies from "../../hooks/MapServiceHooks/useDeliveryAddressCookies";
import { ILatLng, selectIsMobile } from "../../redux/slices/cleverlynkSlice";
import { LupapCityCodes, QueryState, selectDeliveryInfo } from "../../redux/slices/mapServiceSlice";
import ConfirmAddressMap from "../DeliveryMapService/ConfirmAddressMap";
import { IActualMapProvider, IMapProvider } from "../DeliveryMapService/DeliveryInput";
import DynamicAddress from "../DeliveryMapService/DynamicAddress";
import DynamicMap from "../DeliveryMapService/DynamicMap";

import { useTranslation } from "react-i18next";
import "../../i18n/config";

enum AddAddressStage {
  "MAP",
  "SUGGESTIONS",
  "CONFIRM",
}

interface IAddAddress {
  toggleHistoric: () => void;
}

const AddAddress = ({ toggleHistoric }: IAddAddress) => {
  const [stage, setStage] = useState<AddAddressStage>(AddAddressStage.SUGGESTIONS);

  const { cookies, handleAddCookie } = useDeliveryAddressCookies();

  const { t } = useTranslation("deliveryinput");

  const isMobile = useSelector(selectIsMobile);
  const deliveryInfo = useSelector(selectDeliveryInfo);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      indicator: {
        backgroundColor: "red",
      },
    })
  );
  const classes = useStyles();

  const queryLocation = useMemo(
    () => ({ city: deliveryInfo.city ?? "Bogotá", countryCode: deliveryInfo.country ?? "co" }),
    [deliveryInfo]
  );

  const mapProvider: IActualMapProvider = useMemo(() => {
    const coordinates = LupapCityCodes[queryLocation.city] ? IMapProvider.LUPAP : IMapProvider.AWS;
    const autocomplete = IMapProvider.AWS;
    return { coordinates, autocomplete };
  }, [queryLocation]);

  const handleConfirmModifiedLocation = (coordinates: ILatLng) => {
    handleAddCookie({ ...deliveryInfo, coordinates });
    toggleHistoric();
  };

  const handleConfirm = (a?: Partial<QueryState>) => {
    toggleHistoric();
    const elem: any = document.getElementById("map-service-search-field");
    const modifiedAddress = () => ({ address: elem ? elem.value ?? a?.address ?? undefined : a?.address ?? undefined });
    handleAddCookie({ ...a, ...modifiedAddress() });
  };

  const renderStage = useMemo(() => {
    switch (stage) {
      case AddAddressStage.MAP:
        return (
          <DynamicMap
            city={queryLocation.city}
            countryCode={queryLocation.countryCode}
            mapProvider={mapProvider.coordinates}
            toggleSelected={() => handleConfirm()}
          />
        );

      case AddAddressStage.SUGGESTIONS:
        return (
          <DynamicAddress
            toggleMap={() => setStage(AddAddressStage.MAP)}
            toggleSelected={() => setStage(AddAddressStage.CONFIRM)}
            city={queryLocation.city}
            countryCode={queryLocation.countryCode}
            mapProvider={mapProvider}
            citySelector
          />
        );
      case AddAddressStage.CONFIRM:
        return (
          <ConfirmAddressMap
            toggleAddress={() => setStage(AddAddressStage.SUGGESTIONS)}
            toggleSelected={(c: ILatLng) => handleConfirmModifiedLocation(c)}
            bannerPosition={"BOTTOM"}
          />
        );
      default:
        return null;
    }
  }, [queryLocation, mapProvider, stage]);

  const tabs = useMemo(
    () => (
      <Tabs
        key={"add-address-tabs"}
        value={stage}
        onChange={(e, v) => setStage(v)}
        centered={!isMobile}
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons="off"
        classes={{
          indicator: classes.indicator,
        }}>
        <Tab
          value={AddAddressStage.SUGGESTIONS}
          id="input-address"
          label={<Typography style={{ textTransform: "none" }}>{t("addressInput.inputAddress")}</Typography>}
        />
        <Tab
          value={AddAddressStage.MAP}
          id="search-in-map"
          label={<Typography style={{ textTransform: "none" }}>{t("addressInput.searchInMap")}</Typography>}
        />
      </Tabs>
    ),
    [stage, isMobile, classes.indicator]
  );

  return (
    <Grid container justifyContent="center" direction="column" style={{ position: "relative" }}>
      {stage !== AddAddressStage.CONFIRM && tabs}
      {renderStage}
    </Grid>
  );
};
export default AddAddress;
